import { caseFilterSchema } from "@/components/Forms/schema/caseFiltersetSchema.ts";
import { dtMetaSchema } from "@/components/Forms/schema/dtMetaSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createResourceCollectionSelector, handleJsonApiError } from "jsonapi-zod-query";

export const filterResourceSelector = createResourceCollectionSelector({
    type: "caseFilter",
    attributesSchema: caseFilterSchema,
    documentMetaSchema: dtMetaSchema,
});

export type CaseFiltersDocument = ReturnType<typeof filterResourceSelector>;
export type CaseFilter = CaseFiltersDocument["data"][number];

export type CaseFilterEditing = Omit<CaseFilter, "idUUID" | "name"> & {
    idUUID: string | null;
    name?: string;
};

type Props = {
    isQueryEnabled?: boolean;
};

export const useFetchCaseFiltersets = ({
    isQueryEnabled = true,
}: Props): UseQueryResult<CaseFiltersDocument> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        queryKey: ["case-filters"],
        enabled: isQueryEnabled,
        queryFn: async () => {
            const response = await fetch(apiUrl("/dashboard/case-filters"), {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                },
            });

            await handleJsonApiError(response);
            return await response.json();
        },
        select: (data) => {
            try {
                return filterResourceSelector(data);
            } catch (validationError) {
                console.error("Data validation failed:", validationError);
                throw new Error("Received data does not match expected schema");
            }
        },
        staleTime: 10_000,
    });
};
